import { useState, useEffect } from 'react';

import { sizing } from '@mui/system';

import { Box, StyledBoxProps } from '../Box';
import { Skeleton } from '../Skeleton';
import { MediaEmpty, MediaEmptyVariant } from '../MediaEmpty';
import { useIntersectionObserver, useImagePreload } from '../hooks';

import { useStyles } from './ImageWithLoader.styles';

export type ObjectFitType =
  | 'fill'
  | 'contain'
  | 'cover'
  | 'none'
  | 'scale-down';

/** Свойства компонента {@link ImageWithLoader} */
export interface ImageWithLoaderProps {
  /** Определяет источник (URL) изображения. */
  src: string;

  /** Определяет ширину изображения. */
  width: string | number;

  /** Определяет высоту изображения. */
  height: string | number;

  /**
   * Определяет, как содержимое должно заполнять контейнер относительно его высоты и ширины.
   *
   * @defaultValue `'cover'`
   */
  objectFit?: ObjectFitType;

  /** Опциональный внешний индикатор ошибки загрузки. Если true, то будет показан компонент-заглушка. */
  hasError?: boolean;

  /**
   * Вариант empty-состояния при ошибке загрузки.
   *
   * @defaultValue `'single'`
   */
  emptyVariant?: MediaEmptyVariant;

  /** Опциональная callback функция для загрузки медиа в родительском компоненте. Кейс повсеместно встречается в socail разделе приложения. */
  loadImage?: () => Promise<void>;

  /** Опциональная callback функция, которая будет вызвана после успешной загрузки изображения. */
  onLoad?: (event: Event) => void;

  /**
   * Определяет соотношение сторон изображения.
   */
  aspectRatio?: '16/9' | '4/3' | '1/1' | 'auto';
}

/**
 * Медиа компонент ImageWithLoader, который загружает изображение по полученному src, умеет следить
 * за загрузкой и отображать скелетон. При ошибке загрузки отображает компонент-заглушку
 *
 * @public
 * @param src - Определяет источник (URL) изображения.
 * @param width - Определяет ширину изображения.
 * @param height - Определяет высоту изображения.
 * @param objectFit - Определяет, как содержимое должно заполнять контейнер относительно его высоты и ширины.
 * @param hasError - Опциональный внешний индикатор ошибки загрузки. Если true, то будет показан компонент-заглушка.
 * @param emptyVariant - Вариант empty-состояния при ошибке загрузки.
 * @param loadImage - Опциональная callback функция для загрузки медиа в родительском компоненте. Кейс повсеместно встречается в socail разделе приложения.
 * @param aspectRatio - Определяет соотношение сторон изображения.
 * @param onLoad - Опциональная callback функция, которая будет вызвана после успешной загрузки изображения.
 * @returns  JSX элемент, представляющий компонент InfoBox.
 *
 * @remarks
 *
 * Ссылка на свойства компонента {@link ImageWithLoaderProps}
 */
export const ImageWithLoader = ({
  src,
  width,
  height,
  objectFit = 'cover',
  hasError,
  emptyVariant = 'single',
  loadImage,
  onLoad,
  aspectRatio,
}: ImageWithLoaderProps) => {
  const sizeStyles = sizing({ width, height });
  const { classes } = useStyles({ ...sizeStyles, objectFit, aspectRatio });
  const [isIntersected, setIsIntersected] = useState(false);
  const [error, setError] = useState(false);
  const [ref, entry] = useIntersectionObserver({
    threshold: 0,
    root: null,
    rootMargin: '100px',
  });

  const loadedImageStatus = useImagePreload(src, {
    enabled: isIntersected,
    onLoad,
  });

  const baseBoxParams: StyledBoxProps = {
    height,
    width,
    maxWidth: '100%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  };

  useEffect(() => {
    if (entry?.isIntersecting) {
      setIsIntersected(true);
    }
  }, [entry?.isIntersecting]);

  useEffect(() => {
    if (!isIntersected || !loadImage || error) {
      return;
    }

    let isActive = true;

    const load = async () => {
      try {
        await loadImage();

        if (isActive) {
          setError(false);
        }
      } catch {
        if (isActive) {
          setError(true);
        }
      }
    };

    load();

    return () => {
      isActive = false;
    };
  }, [loadImage, isIntersected, error]);

  if (loadedImageStatus === 'complete') {
    return (
      <Box
        {...baseBoxParams}
        position="relative"
        bgcolor="bg.dark.primary"
        overflow="hidden"
        radius="m"
        testId="image-with-loader-test-id"
        className={classes.wrapper}
      >
        <img
          src={src}
          alt="cover"
          width="100%"
          height="100%"
          className={classes.img}
        />
      </Box>
    );
  }

  if (loadedImageStatus === 'failed' || hasError || error) {
    return (
      <Box className={classes.wrapper}>
        <MediaEmpty
          variant={emptyVariant}
          width={width}
          height={height}
          radius="m"
          testId="image-with-loader-failed-test-id"
        />
      </Box>
    );
  }

  return (
    <>
      <Box ref={ref} component="span" position="absolute" />
      <Box
        testId="image-with-loader-skeleton-test-id"
        className={classes.wrapper}
      >
        <Skeleton height={height} width={width} variant="rect" />
      </Box>
    </>
  );
};
